import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, MailWarning, Pencil, X } from 'lucide-react';
import { previewReminder, sendReminder, type ReminderPreview } from '../../lib/invoiceRemindersApi';
import { useI18n } from '../../contexts/I18nContext';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { Select } from '../ui/Select';

export interface SendReminderModalProps {
  open: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
}

/**
 * Send-reminder modal (overdue invoices widget). On open it asks the backend
 * to pre-fill subject/body from the editable 'overdue_invoice' template
 * (rendering lives server-side only); the user can adjust the recipient,
 * subject and HTML body, toggle an iframe preview, then send.
 */
export function SendReminderModal({ open, clientId, clientName, onClose }: SendReminderModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const activeCompanyId = useActiveCompanyId() ?? undefined;

  const [preview, setPreview] = useState<ReminderPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Server-side pre-fill on open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setLoadError(null);
    setShowPreview(false);
    setSendError(null);
    previewReminder(clientId, activeCompanyId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setTo(data.to ?? data.emailCandidates[0] ?? '');
        setSubject(data.subject);
        setBodyHtml(data.bodyHtml);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : t('reminder.modal.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId, activeCompanyId, t]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const canSubmit =
    Boolean(preview) && Boolean(to) && subject.trim().length > 0 && bodyHtml.trim().length > 0;

  async function handleSend() {
    if (!preview || !canSubmit || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await sendReminder({
        clientId,
        ...(activeCompanyId ? { companyId: activeCompanyId } : {}),
        to,
        subject: subject.trim(),
        bodyHtml,
        invoiceIds: preview.invoiceIds,
      });
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'overdue-invoices'] });
      onClose();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : t('reminder.modal.error'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-reminder-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[720px] max-h-[90vh] p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2
            id="send-reminder-title"
            className="flex items-center gap-2 text-lg font-semibold text-gray-900"
          >
            <MailWarning size={18} className="text-brand-600" />
            {t('reminder.modal.title', { client: clientName })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>

        {loadError ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">{loadError}</div>
        ) : !preview ? (
          <div className="px-5 py-8 text-center text-sm text-gray-500">
            {t('reminder.modal.loading')}
          </div>
        ) : (
          <>
            <div className="max-h-[calc(90vh-9rem)] overflow-y-auto px-5 py-4">
              {/* Invoice summary */}
              <p className="text-sm text-gray-600">
                {t('reminder.modal.summary', {
                  count: String(preview.invoiceCount),
                  total: `${new Intl.NumberFormat('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(preview.totalOverdue)} ${preview.currency}`,
                })}
              </p>

              {/* Recipient */}
              <div className="mt-4">
                <Select
                  label={t('reminder.modal.to')}
                  value={to}
                  onChange={setTo}
                  options={preview.emailCandidates.map((email) => ({
                    value: email,
                    label: email,
                  }))}
                />
              </div>

              {/* Subject */}
              <label className="mt-4 block text-sm font-medium text-gray-700">
                {t('reminder.modal.subject')}
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  className="ui-input mt-1"
                />
              </label>

              {/* Body: HTML editor <-> iframe preview toggle */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {t('reminder.modal.body')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline"
                >
                  {showPreview ? <Pencil size={12} /> : <Eye size={12} />}
                  {showPreview ? t('reminder.modal.edit') : t('reminder.modal.preview')}
                </button>
              </div>
              {showPreview ? (
                <iframe
                  title={t('reminder.modal.preview')}
                  srcDoc={bodyHtml}
                  sandbox=""
                  className="mt-1 h-72 w-full rounded-lg border border-border bg-white"
                />
              ) : (
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="ui-textarea mt-1 resize-y font-mono text-xs"
                />
              )}

              {sendError && <p className="mt-4 text-sm text-red-600">{sendError}</p>}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <button type="button" onClick={onClose} className="ui-btn-secondary">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSubmit || sending}
                className="ui-btn-primary"
              >
                {sending ? t('reminder.modal.sending') : t('reminder.modal.send')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
